import { memo } from 'react';
import { Box, Container, Header, Link, SpaceBetween, Table } from '@amzn/awsui-components-core';
import { CodeRepoReviewStatus } from '@amzn/awscat-pattern-library-service-model';

import { CodeReadonlyRow, useCodeReadonlyTabViewModel } from './CodeReadonlyTabViewModel';
import { CodeRepoStatusBadge } from '../v2-common/CodeRepoStatusBadge';

/** Review status badge, with the review ticket link beneath it when one exists and may be shown. */
function ReviewStatusCell({
  status,
  ticketUrl,
  ticketLinkText,
  isTicketLinkVisible,
}: {
  readonly status: CodeRepoReviewStatus;
  readonly ticketUrl: string;
  readonly ticketLinkText: string;
  readonly isTicketLinkVisible: boolean;
}) {
  if (!isTicketLinkVisible || !ticketUrl) return <CodeRepoStatusBadge status={status} />;
  return (
    <SpaceBetween size="xxxs">
      <CodeRepoStatusBadge status={status} />
      <Link href={ticketUrl} external fontSize="body-s">
        {ticketLinkText}
      </Link>
    </SpaceBetween>
  );
}

/** Read-only Code tab: lists code repositories and their review status, no add/edit/delete. */
export const CodeReadonlyTabView = memo(function CodeReadonlyTabView() {
  const vm = useCodeReadonlyTabViewModel();

  if (!vm) return null;

  return (
    <Container
      header={
        <Header variant="h3" counter={vm.rows.length > 0 ? `(${vm.rows.length})` : undefined}>
          Code repository
        </Header>
      }
    >
      {vm.rows.length > 0 ? (
        <Table<CodeReadonlyRow>
          columnDefinitions={[
            {
              id: 'label',
              header: 'Title',
              cell: (row) =>
                row.url ? (
                  <Link href={row.url} external>
                    {row.label || row.url}
                  </Link>
                ) : (
                  <Box>{row.label || '—'}</Box>
                ),
            },
            { id: 'branch', header: 'Branch', cell: (row) => row.branch || '—' },
            { id: 'type', header: 'Type', cell: (row) => row.repoType },
            {
              id: 'appsec',
              header: 'Security review',
              cell: (row) => (
                <ReviewStatusCell
                  status={row.appsecStatus}
                  ticketUrl={row.appsecTicketUrl}
                  ticketLinkText="AppSec review ticket"
                  isTicketLinkVisible={vm.isReviewTicketLinkVisible}
                />
              ),
            },
            {
              id: 'opensource',
              header: 'Open source',
              cell: (row) =>
                vm.isExternalAudience ? (
                  <ReviewStatusCell
                    status={row.openSourceStatus}
                    ticketUrl={row.openSourceTicketUrl}
                    ticketLinkText="Open source review ticket"
                    isTicketLinkVisible={vm.isReviewTicketLinkVisible}
                  />
                ) : (
                  '—'
                ),
            },
          ]}
          items={vm.rows}
          trackBy="key"
          variant="embedded"
        />
      ) : (
        <Box textAlign="center" color="text-body-secondary" padding={{ vertical: 'xxl' }}>
          No repositories.
        </Box>
      )}
    </Container>
  );
});
